import { createContext, useContext, useMemo, useState } from 'react';

export type ErrorBoundaryContextType = {
  didCatch: boolean;
  error: Error;
  resetErrorBoundary: (...args: unknown[]) => void;
};

export const ErrorBoundaryContext = createContext<ErrorBoundaryContextType | null>(null);

type UseErrorBoundaryState<TError> = { error: TError; hasError: true } | { error: null; hasError: false };

export type UseErrorBoundaryApi<T> = {
  resetBoundary: () => void;
  showBoundary: (error: T) => void;
};

export function useErrorBoundary<T = Error>(): UseErrorBoundaryApi<T> {
  const context = useContext(ErrorBoundaryContext);

  if (context === null) {
    throw new Error('useErrorBoundary must be used within an ErrorBoundaryContext');
  }

  const [state, setState] = useState<UseErrorBoundaryState<T>>({
    error: null,
    hasError: false,
  });

  const memoized = useMemo(
    () => ({
      resetBoundary: () => {
        context.resetErrorBoundary();
        setState({ error: null, hasError: false });
      },
      showBoundary: (error: T) =>
        setState({
          error,
          hasError: true,
        }),
    }),
    [context],
  );

  if (state.hasError) {
    throw state.error;
  }

  return memoized;
}
